import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CreditCard, ExternalLink, AlertTriangle, CheckCircle2, ArrowDownCircle, Loader2 } from 'lucide-react';
import { useSubscription } from '../../hooks/useSubscription';
import { usePlan } from '../../hooks/usePlan';
import { useToast } from '../ui/Toast';
import { DowngradeConfirmationModal } from '../billing/DowngradeConfirmationModal';
import type { PlanTier } from '../../lib/plans/types';

const TIER_LABELS: Record<PlanTier, string> = {
  free: 'Plano Free',
  pro: 'Plano Pro',
  business: 'Plano Business',
};

export const BillingPanel: React.FC = () => {
  const { subscription, redirectToPortal, scheduleDowngrade, resumeSubscription } = useSubscription();
  const { effectiveTier, loading } = usePlan();
  const { showToast } = useToast();

  const [downgradeModalOpen, setDowngradeModalOpen] = useState(false);
  const [resuming, setResuming] = useState(false);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const hasStripeSub =
    subscription !== null && ['active', 'trialing', 'past_due'].includes(subscription.status);
  const isDowngradeScheduled = hasStripeSub && subscription?.cancel_at_period_end === true;
  const canDowngrade =
    hasStripeSub &&
    !isDowngradeScheduled &&
    (effectiveTier === 'pro' || effectiveTier === 'business');

  // Downgrade path: Pro → Free; Business → Pro (um degrau por vez)
  const downgradeTarget: 'free' | 'pro' = effectiveTier === 'business' ? 'pro' : 'free';

  const handleConfirmDowngrade = async () => {
    const { error } = await scheduleDowngrade(downgradeTarget);
    if (error) {
      showToast('error', `Falha ao agendar downgrade: ${error}`);
      throw new Error(error);
    }
    const whenStr = subscription?.current_period_end
      ? formatDate(subscription.current_period_end)
      : 'fim do ciclo atual';
    showToast('success', `Downgrade agendado. Mudança em ${whenStr}.`);
    setDowngradeModalOpen(false);
  };

  const handleResumeSubscription = async () => {
    if (resuming) return;
    setResuming(true);
    try {
      const { error } = await resumeSubscription();
      if (error) {
        showToast('error', `Falha ao cancelar downgrade: ${error}`);
      } else {
        showToast('success', 'Downgrade cancelado — sua assinatura continua ativa.');
      }
    } finally {
      setResuming(false);
    }
  };

  return (
    <div className="liquid-glass rounded-2xl p-6">
      <div className="flex items-center gap-2 mb-4">
        <CreditCard className="w-5 h-5 text-primary-400" />
        <h2 className="text-lg font-semibold text-white">Plano e Cobranca</h2>
      </div>

      {loading ? (
        <p className="text-neutral-400 text-sm">Carregando…</p>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            <span className="px-3 py-1 rounded-full text-sm font-medium bg-emerald-500/20 text-emerald-400">
              {TIER_LABELS[effectiveTier]}
            </span>
            {isDowngradeScheduled && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/15 text-amber-400 border border-amber-500/30">
                downgrade agendado
              </span>
            )}
          </div>

          {hasStripeSub && subscription?.current_period_end && (
            <p className="text-neutral-500 text-sm mb-1">
              {subscription.cancel_at_period_end
                ? `Acesso ate ${formatDate(subscription.current_period_end)}`
                : `Proxima cobranca: ${formatDate(subscription.current_period_end)}`}
            </p>
          )}

          {isDowngradeScheduled && subscription?.current_period_end && (
            <div className="flex items-start gap-2 my-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <p className="text-amber-300 font-medium">Cancelamento agendado</p>
                <p className="text-amber-200/80 text-xs mt-0.5">
                  Sua assinatura será encerrada em {formatDate(subscription.current_period_end)}.
                  Você mantém acesso {TIER_LABELS[effectiveTier]} até lá.
                </p>
              </div>
            </div>
          )}

          {subscription?.status === 'past_due' && (
            <div className="flex items-center gap-2 my-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <span className="text-red-400 text-sm font-medium">Pagamento pendente</span>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 mt-4">
            {hasStripeSub ? (
              <button
                onClick={redirectToPortal}
                className="flex items-center justify-center gap-2 px-4 py-3 sm:py-2 rounded-lg bg-neutral-800 text-white text-sm font-medium hover:bg-neutral-700 transition-colors border border-neutral-700"
              >
                Gerenciar assinatura
                <ExternalLink className="w-3.5 h-3.5" />
              </button>
            ) : null}

            {effectiveTier !== 'business' && !isDowngradeScheduled && (
              <Link
                to="/pricing"
                className="flex items-center justify-center gap-2 px-4 py-3 sm:py-2 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors"
              >
                {effectiveTier === 'free' ? 'Ver planos' : 'Fazer upgrade'}
              </Link>
            )}

            {isDowngradeScheduled && (
              <button
                type="button"
                onClick={handleResumeSubscription}
                disabled={resuming}
                className="flex items-center justify-center gap-2 px-4 py-3 sm:py-2 rounded-lg bg-emerald-500 text-white text-sm font-medium hover:bg-emerald-600 transition-colors disabled:opacity-50"
              >
                {resuming ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Cancelando...
                  </>
                ) : (
                  'Cancelar downgrade'
                )}
              </button>
            )}
          </div>

          {canDowngrade && (
            <div className="mt-3 pt-3 border-t border-white/5">
              <button
                type="button"
                onClick={() => setDowngradeModalOpen(true)}
                className="inline-flex items-center gap-1.5 text-xs text-neutral-500 hover:text-neutral-300 transition-colors"
              >
                <ArrowDownCircle className="w-3.5 h-3.5" />
                Fazer downgrade
              </button>
            </div>
          )}
        </>
      )}

      {canDowngrade && (effectiveTier === 'pro' || effectiveTier === 'business') && (
        <DowngradeConfirmationModal
          isOpen={downgradeModalOpen}
          onClose={() => setDowngradeModalOpen(false)}
          currentTier={effectiveTier}
          targetTier={downgradeTarget}
          periodEnd={subscription?.current_period_end ?? null}
          onConfirm={handleConfirmDowngrade}
        />
      )}
    </div>
  );
};
